/* eslint-disable react-native/no-inline-styles */
import { Box, Text, Button, Row, Pressable, FormControl } from 'native-base'
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { Modal, Platform } from 'react-native'

import {
  CheckBoxList,
  ControlledField,
  Icon,
  KeyboardAwareScrollView,
} from '~components'
import { useTranslation } from '~hooks'
import { useRewardsPopupForm } from '~hooks/forms/useRewardsPopupForm'
import {
  useGetCountriesList,
  useGetInterestsList,
  useGetStatesList,
  useGetCitiesList,
} from '~query-hooks'

type RewardsPopupProps = {
  isVisible: boolean
  onClose: () => void
  onComplete: () => void
  user: UserResponse
}

export const RewardsPopup = ({
  isVisible,
  onClose,
  onComplete,
  user,
}: RewardsPopupProps): JSX.Element => {
  const { t } = useTranslation()
  const { countries: locationList } = useGetCountriesList({ enabled: isVisible })
  const { interests: interestsList } = useGetInterestsList({ enabled: isVisible })
  const {
    control,
    errors,
    submit,
    isSubmitting,
    setValue,
    getValues,
    hasAnyFieldData,
    clearErrors,
    handleZipChange,
    handleZipBlur,
    isLocationLoading,
  } = useRewardsPopupForm()
  const [localInterests, setLocalInterests] = useState<string[]>([])
  const selectedCountryValue = useWatch({ control, name: 'countryId' }) as string | undefined
  const selectedStateId = useWatch({ control, name: 'stateId' }) as string | undefined
  const normalizedCountryId = useMemo(() => {
    if (!selectedCountryValue) {
      return ''
    }
    const match = locationList?.find((option) => option.value === selectedCountryValue)
    return match ? match.value : ''
  }, [locationList, selectedCountryValue])

  const { states: statesList } = useGetStatesList(normalizedCountryId, {
    enabled: isVisible && Boolean(normalizedCountryId),
  })
  const { cities: citiesList } = useGetCitiesList(selectedStateId, {
    enabled: isVisible && Boolean(selectedStateId),
  })

  const handleDatePickerConfirm = useCallback(
    (selectedDate: Date) => {
      // Handle date picker confirmation - focus next field if needed
    },
    []
  )

  const handleInterestPress = useCallback(
    (value: string) => {
      const currentInterests = getValues('interests') || []
      const isAlreadySelected = currentInterests.includes(value)
      const newInterests = isAlreadySelected
        ? currentInterests.filter((interest) => interest !== value)
        : [...currentInterests, value]
      setLocalInterests(newInterests)
      setValue('interests', newInterests, { shouldValidate: false })
    },
    [getValues, setValue]
  )

  const handleSaveButtonPress = useCallback(async () => {
    const submissionResult = await submit()
    if (submissionResult) {
      setLocalInterests([])
      onComplete()
    }
  }, [submit, onComplete])

  useEffect(() => {
    if (!statesList?.length) {
      return
    }

    const currentStateId = getValues('stateId')
    const currentStateName = getValues('state')
    const matchedState = currentStateId
      ? statesList.find((stateOption) => stateOption.value === currentStateId)
      : statesList.find((stateOption) => stateOption.label === currentStateName)
    if (matchedState && getValues('stateId') !== matchedState.value) {
      setValue('stateId', matchedState.value, { shouldValidate: false })
    }
    if (matchedState && getValues('state') !== matchedState.label) {
      setValue('state', matchedState.label, { shouldValidate: false })
    }
  }, [statesList, getValues, setValue])

  useEffect(() => {
    if (!citiesList?.length) {
      return
    }

    const currentCityId = getValues('cityId')
    const currentCityName = getValues('city')
    const matchedCity = currentCityId
      ? citiesList.find((cityOption) => cityOption.value === currentCityId)
      : citiesList.find((cityOption) => cityOption.label === currentCityName)
    if (matchedCity && getValues('cityId') !== matchedCity.value) {
      setValue('cityId', matchedCity.value, { shouldValidate: false })
    }
    if (matchedCity && getValues('city') !== matchedCity.label) {
      setValue('city', matchedCity.label, { shouldValidate: false })
    }
  }, [citiesList, getValues, setValue])

  // Initialize form when modal opens (only once)
  useEffect(() => {
    if (isVisible) {
      const formInterests = getValues('interests') || []
      setLocalInterests(formInterests)
      // Clear any existing errors since all fields are optional
      clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]) // Only run when modal becomes visible

  const [isSelectGenderFocused, setIsSelectGenderFocused] = useState<boolean>(false)
  const [isSelectLocationFocused, setIsSelectedLocationFocused] = useState<boolean>(false)
  const birthDateRef = useRef<() => void>(() => {
    // Date picker ref handler
  })

  const closeGenderPicker = useCallback(() => {
    setIsSelectGenderFocused(false)
  }, [])

  const closeLocationPicker = useCallback(() => {
    setIsSelectedLocationFocused(false)
  }, [])

  const handleGenderPickerValueChange = useCallback(() => {
    closeGenderPicker()
  }, [closeGenderPicker])

  const openGenderPicker = useCallback(() => {
    setIsSelectGenderFocused(true)
  }, [])

  const openLocationPicker = useCallback(() => {
    setIsSelectedLocationFocused(true)
  }, [])

  const handleCountryChange = useCallback(
    (value: string) => {
      setValue('countryId', value, { shouldValidate: false })
      setValue('location', value, { shouldValidate: false })
      setValue('stateId', '', { shouldValidate: false })
      setValue('state', '', { shouldValidate: false })
      setValue('cityId', '', { shouldValidate: false })
      setValue('city', '', { shouldValidate: false })
      closeLocationPicker()
    },
    [closeLocationPicker, setValue]
  )

  const handleStateChange = useCallback(
    (value: string) => {
      const normalizedValue = value?.toString?.() ?? ''
      const selectedState = statesList?.find((item) => item.value === normalizedValue)
      setValue('stateId', normalizedValue, { shouldValidate: false })
      setValue('state', selectedState?.label || '', { shouldValidate: false })
      setValue('cityId', '', { shouldValidate: false })
      setValue('city', '', { shouldValidate: false })
      if (!selectedState) {
        setValue('stateId', '', { shouldValidate: false })
      }
    },
    [setValue, statesList]
  )

  const handleCityChange = useCallback(
    (value: string) => {
      const normalizedValue = value?.toString?.() ?? ''
      const selectedCity = citiesList?.find((item) => item.value === normalizedValue)
      setValue('cityId', normalizedValue, { shouldValidate: false })
      setValue('city', selectedCity?.label || '', { shouldValidate: false })
      if (!selectedCity) {
        setValue('cityId', '', { shouldValidate: false })
      }
    },
    [citiesList, setValue]
  )

  const isSubmitDisabled = isSubmitting || !hasAnyFieldData

  return (
    <Modal
      visible={isVisible}
      animationType="slide"
      presentationStyle="overFullScreen"
      onRequestClose={onClose}
      transparent={true}
      statusBarTranslucent={false}
    >
      <Box flex={1} bg="rgba(0, 0, 0, 0.5)" justifyContent="center" alignItems="center">
        <Box
          flex={1}
          bg="white"
          w={Platform.OS === 'web' ? '400px' : '100%'}
          maxH="90%"
          maxW={Platform.OS === 'web' ? '400px' : '100%'}
        >
          <KeyboardAwareScrollView
            contentContainerStyle={{ flexGrow: 1 }}
            extraScrollHeight={10}
            keyboardShouldPersistTaps="handled"
            removeClippedSubviews={false}
            scrollEventThrottle={16}
            enableOnAndroid={true}
            enableAutomaticScroll={true}
          >
            <Box flex={1} p="4">
              {/* Header */}
              <Row justifyContent="space-between" alignItems="center" mb="4">
                 <Text fontSize="xl" bold>
                 {(user && user.basicInfo && user.basicInfo.profileUpdated == 1) ? '' : t('rewards_popup.title')}
                </Text>
                <Pressable onPress={onClose} p="2">
                  <Icon name="close-line" size={24} color="black" />
                </Pressable>
              </Row>

              {/* Rewards Banner */}
              {!(user && user.basicInfo && user.basicInfo.profileUpdated == 1) && <Box
                bg="actionBlue"
                rounded="lg"
                p="4"
                mb="6"
                flexDirection="row"
                alignItems="center"
              >
                <Box
                  w="12"
                  h="12"
                  bg="white"
                  rounded="full"
                  alignItems="center"
                  justifyContent="center"
                  mr="3"
                >
                  <Text fontSize="lg" bold color="actionBlue">
                    R
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text color="white" fontSize="lg" bold mb="1">
                    {t('rewards_popup.banner_title')}
                  </Text>
                  <Text color="white" fontSize="sm" opacity={0.9}>
                    {t('rewards_popup.banner_description')}
                  </Text>
                </Box>
              </Box>}

              {/* Form */}
              <Box w="full" mb="6">
                <Text fontSize="lg" bold mb="6">
                  {t('rewards_popup.form_title')}
                </Text>

                <ControlledField.Input
                  {...{ control, errors }}
                  label={t('common.zip_code')}
                  name="zipCode"
                  placeholder={
                    isLocationLoading ? t('form.location_fetching') : t('form.user_details.zip_code_placeholder')
                  }
                  testID="zipCodeInput"
                  onBlur={handleZipBlur}
                  onChangeText={handleZipChange}
                  editable={!isLocationLoading}
                />

                <ControlledField.Select
                  {...{ control, errors }}
                  isFocused={isSelectLocationFocused}
                  label={t('common.country')}
                  name="countryId"
                  onClose={closeLocationPicker}
                  onOpen={openLocationPicker}
                  onValueChange={handleCountryChange}
                  placeholder={
                    isLocationLoading ? t('form.location_fetching') : t('form.user_details.country_placeholder')
                  }
                  size="xl"
                  values={locationList || []}
                  isDisabled={isLocationLoading}
                />

                <ControlledField.Select
                  {...{ control, errors }}
                  label={t('common.state')}
                  name="stateId"
                  placeholder={
                    isLocationLoading ? t('form.location_fetching') : t('form.user_details.state_placeholder')
                  }
                  size="xl"
                  values={statesList || []}
                  isDisabled={isLocationLoading || !statesList?.length}
                  onValueChange={handleStateChange}
                />

                <ControlledField.Select
                  {...{ control, errors }}
                  label={t('common.city')}
                  name="cityId"
                  placeholder={
                    isLocationLoading ? t('form.location_fetching') : t('form.user_details.city_placeholder')
                  }
                  size="xl"
                  values={citiesList || []}
                  isDisabled={isLocationLoading || !citiesList?.length}
                  onValueChange={handleCityChange}
                />

                <ControlledField.Input
                  {...{ control, errors }}
                  keyboardType="numeric"
                  label={t('common.birth_date')}
                  maxLength={4}
                  name="birthDate"
                  placeholder="YYYY"
                  testID="birthDateInput"
                />

                <ControlledField.Select
                  {...{ control, errors }}
                  onClose={closeGenderPicker}
                  onOpen={openGenderPicker}
                  isFocused={isSelectGenderFocused}
                  label={t('common.gender')}
                  name="gender"
                  onValueChange={handleGenderPickerValueChange}
                  placeholder={t('form.user_details.gender_placeholder')}
                  size="xl"
                  values={[
                    { label: t('form.user_details.gender_picker_female'), value: 'female' },
                    { label: t('form.user_details.gender_picker_male'), value: 'male' },
                    { label: t('form.user_details.gender_picker_other'), value: 'other' },
                  ]}
                />

                <FormControl isInvalid={!!errors.interests} mt="4">
        <FormControl.Label>
          <Text fontSize="md" mb="3" color="black">
            {t('form.user_details.interests_label')} (Optional)
          </Text>
        </FormControl.Label>
                  <CheckBoxList
                    layout="grid"
                    checkBoxList={
                      interestsList?.map((interest) => ({
                        label: interest.label,
                        value: interest.value,
                        isChecked: localInterests.includes(interest.value),
                      })) || []
                    }
                    onPress={handleInterestPress}
                  />
                  <FormControl.ErrorMessage>{errors.interests?.message}</FormControl.ErrorMessage>
                </FormControl>
              </Box>

              {/* Save Button */}
              <Button
                bg="actionBlue"
                isDisabled={isSubmitDisabled}
                isLoading={isSubmitting}
                onPress={handleSaveButtonPress}
                size="lg"
                testID="saveButton"
                mb="4"
              >
                <Text bold color="white" fontSize="md">
                  {t('rewards_popup.save_button')}
                </Text>
              </Button>
            </Box>
          </KeyboardAwareScrollView>
        </Box>
      </Box>
    </Modal>
  )
}
